import { pgTable, text, timestamp, jsonb, uniqueIndex, integer, foreignKey } from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { file } from './files';

/**
 * SystemSetting - Системные настройки платформы
 * Хранит ключ-значение настройки с метаданными
 */
export const systemSetting = pgTable('SystemSetting', {
  key: text('key').primaryKey(),                          // Уникальный ключ настройки
  value: text('value').notNull(),                         // Значение настройки
  description: text('description'),                       // Описание настройки
  metadata: jsonb('metadata'),                            // Дополнительные метаданные
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).$onUpdate(() => new Date()).notNull(),
}, (table) => [
  uniqueIndex('SystemSetting_key_idx').using('btree', table.key),
]);

/**
 * ClubSettings - Настройки клуба (singleton)
 * Хранит единую конфигурацию клуба
 */
export const clubSettings = pgTable('ClubSettings', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  bannerImages: jsonb(),                                  // { mobile: string; tablet: string; desktop: string } (legacy)
  bannerMobileFileId: integer(),                          // FK → File (баннер mobile)
  bannerTabletFileId: integer(),                          // FK → File (баннер tablet)
  bannerDesktopFileId: integer(),                         // FK → File (баннер desktop)
  missionAuthorTeacherId: integer(),                      // ID учителя — автора миссии
  missionAuthorPhotoUrl: text(),                          // URL фотографии автора миссии (legacy)
  missionAuthorPhotoId: integer(),                        // FK → File (фото автора миссии)
  experienceYears: integer().default(0).notNull(),        // Количество лет опыта
  experienceImageUrl: text(),                             // URL изображения опыта (legacy)
  experienceImageId: integer(),                           // FK → File (изображение опыта)
  stats: jsonb().notNull(),                               // { researchCount: number; practiceHours: number; specialistHours: number; countriesCount: number }
  spheres: jsonb().notNull(),                             // [{ icon: string; title: string; subtitle: string }]
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).$onUpdate(() => new Date()).notNull(),
}, (table) => [
  foreignKey({
    columns: [table.bannerMobileFileId],
    foreignColumns: [file.id],
    name: 'ClubSettings_bannerMobileFileId_fkey',
  }).onUpdate('cascade').onDelete('set null'),
  foreignKey({
    columns: [table.bannerTabletFileId],
    foreignColumns: [file.id],
    name: 'ClubSettings_bannerTabletFileId_fkey',
  }).onUpdate('cascade').onDelete('set null'),
  foreignKey({
    columns: [table.bannerDesktopFileId],
    foreignColumns: [file.id],
    name: 'ClubSettings_bannerDesktopFileId_fkey',
  }).onUpdate('cascade').onDelete('set null'),
  foreignKey({
    columns: [table.missionAuthorPhotoId],
    foreignColumns: [file.id],
    name: 'ClubSettings_missionAuthorPhotoId_fkey',
  }).onUpdate('cascade').onDelete('set null'),
  foreignKey({
    columns: [table.experienceImageId],
    foreignColumns: [file.id],
    name: 'ClubSettings_experienceImageId_fkey',
  }).onUpdate('cascade').onDelete('set null'),
]);

/**
 * Известные ключи системных настроек
 */
export const SYSTEM_SETTING_KEYS = {
  /** Базовая валюта для аналитики (USD, UZS, RUB) */
  ANALYTICS_BASE_CURRENCY: 'analytics.baseCurrency',

  // ==================== LIVESTREAM ====================
  /** Максимум одновременных зрителей на одну трансляцию. По умолчанию 100. */
  LIVESTREAM_MAX_CONCURRENT_VIEWERS: 'livestream.maxConcurrentViewers',
  /** Верхний предел bitrate публикации RTMP (Mbps). По умолчанию 10. */
  LIVESTREAM_MAX_BITRATE_MBPS: 'livestream.maxBitrateMbps',
  /** Задержка перед проверкой bitrate после старта (сек). По умолчанию 5. */
  LIVESTREAM_BITRATE_CHECK_DELAY_SECONDS: 'livestream.bitrateCheckDelaySeconds',
  /** Grace period на переподключение RTMP (сек) перед переводом в ENDED. */
  LIVESTREAM_RECONNECT_GRACE_SECONDS: 'livestream.reconnectGraceSeconds',

  // ==================== REGISTRATION ====================
  /**
   * JSON-массив языковых кодов, доступных для выбора при регистрации.
   * Пример: '["ru","uz","en"]'. Если ключа нет или value пустое — доступны все
   * языки из локализации. Используется платформой и ботом.
   */
  REGISTRATION_AVAILABLE_LANGUAGES: 'registration.availableLanguages',
  /**
   * JSON-массив идентификаторов Country, доступных для выбора при регистрации.
   * Пример: '[1,2,5]'. Если ключа нет, value пустое или value === 'all' —
   * доступны все isActive=true страны. Используется платформой и ботом.
   */
  REGISTRATION_AVAILABLE_COUNTRY_IDS: 'registration.availableCountryIds',

  // ==================== FISCAL (54-ФЗ) ====================
  /**
   * Активный провайдер фискализации: 'CLOUDPAYMENTS' (CP сам печатает чек,
   * customerReceipt в JsonData) или 'BUSINESS_RU' (мы отправляем чек через
   * @masters-club/business-ru, customerReceipt в CP отключён).
   * Значение должно совпадать с FiscalProvider из @masters-club/types.
   */
  FISCAL_PROVIDER: 'fiscal.provider',
  /**
   * Базовый URL Бизнес.ру API (со слэшем на конце).
   * test: https://check-dev.business.ru/open-api/v1/
   * prod: https://check.business.ru/open-api/v1/
   */
  BUSINESS_RU_ACCOUNT_URL: 'fiscal.businessRu.accountUrl',
  /** app_id интеграции Бизнес.ру (создаётся в ЛК → Внешние интеграции). */
  BUSINESS_RU_APP_ID: 'fiscal.businessRu.appId',
  /** Secret key интеграции (шифруется через @masters-club/crypto перед сохранением). */
  BUSINESS_RU_SECRET: 'fiscal.businessRu.secret',
  /** ФИО кассира (попадает в чек как author). По умолчанию "MastersClub Online". */
  BUSINESS_RU_CASHIER: 'fiscal.businessRu.cashier',
  /**
   * СНО по стандарту ФНС (0..5): 0=ОСН, 1=УСН-Д, 2=УСН-ДР, 3=ЕНВД, 4=ЕСХН, 5=Патент.
   * Адаптер переводит в bit-флаг Бизнес.ру (tag1055).
   */
  BUSINESS_RU_TAXATION_SYSTEM: 'fiscal.businessRu.taxationSystem',
  /**
   * Ставка НДС: null | 0 | 10 | 20 | 110 | 120. Для УСН — null (флаг nds_not_apply).
   * Хранится как строка ('null' / число).
   */
  BUSINESS_RU_VAT_RATE: 'fiscal.businessRu.vatRate',
  /**
   * Callback URL для async-уведомления о фискализации. По умолчанию
   * `<BACKEND_BASE_URL>/api/fiscal/business-ru/callback`.
   * Если пусто — клиент использует только polling.
   */
  BUSINESS_RU_CALLBACK_URL: 'fiscal.businessRu.callbackUrl',
  /**
   * Префикс c_num (идентификатор чека в Бизнес.ру) и идемпотентного ключа.
   * Формат: `<prefix><orderId>` для SELL, `<prefix><orderId>_r` для REFUND,
   * `<prefix><orderId>_c` для CORRECTION_SELL, `<prefix><orderId>_cr` для CORRECTION_REFUND.
   * Дефолт: `club`. Примеры значений: `club`, `mc_platform`, `shop1`.
   * Используется чтобы отличать чеки нашего проекта от других интеграций
   * на одном и том же аккаунте Бизнес.ру.
   */
  FISCAL_CNUM_PREFIX: 'fiscal.cnumPrefix',

  // ==================== TENANT (branding + feature flags) ====================
  /**
   * JSON feature-flags map: `{ subscriptions: true, livestreams: false, ... }`.
   * Missing key in the object is treated as `true` (feature enabled). If the row
   * is absent entirely, all features are enabled (single-tenant club без конфигурации).
   * Consumed by `/public/tenant-config` (platform/admin) and `requireFeature()` on routes.
   */
  TENANT_FEATURE_FLAGS: 'tenant.featureFlags',
  /**
   * JSON branding object: `{ name?: string; logoUrl?: string; brandColor?: string }`.
   * Overrides registry defaults (TenantDescriptor.name/logoUrl). If the row is absent
   * or fields are empty — the frontend uses its built-in fallback (current logo/name).
   */
  TENANT_BRANDING: 'tenant.branding',
} as const;

// ==================== LIVESTREAM DEFAULTS ====================
/**
 * Дефолты для livestream settings. Используются, когда запись в
 * SystemSetting отсутствует. Значения — строки, т.к. таблица хранит text.
 */
export const LIVESTREAM_SETTING_DEFAULTS = {
  [SYSTEM_SETTING_KEYS.LIVESTREAM_MAX_CONCURRENT_VIEWERS]: '100',
  [SYSTEM_SETTING_KEYS.LIVESTREAM_MAX_BITRATE_MBPS]: '10',
  [SYSTEM_SETTING_KEYS.LIVESTREAM_BITRATE_CHECK_DELAY_SECONDS]: '5',
  [SYSTEM_SETTING_KEYS.LIVESTREAM_RECONNECT_GRACE_SECONDS]: '60',
} as const;

export type SystemSettingKey = typeof SYSTEM_SETTING_KEYS[keyof typeof SYSTEM_SETTING_KEYS];
